import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { ServicesBuilder } from '../utils/setup';
import { Employee, EmployeeRole, EmployeeRoleService, EmployeeService } from '@posx/core';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { useAcl } from './acl';
import { PresentationService } from '@/services/presentation.service';
import { logLiveQueryError, isLiveQueryCursorError } from '@/utils/logger';
import { Subscription } from 'dexie';
import { getPlatform } from '@/utils/misc';

export const STAFF_KEY = '_STAFF_';

type ExtendedEmployee = Employee & { role?: EmployeeRole };

// attach role by role_uid; roleService is guaranteed non-null by callers
async function enrichEmployeeRole(item: Employee, roleService: EmployeeRoleService): Promise<ExtendedEmployee> {
  const employee = item as ExtendedEmployee;
  if (item.role_uid) employee.role = await roleService.getOne(item.role_uid) || undefined;
  return employee;
}

export const useEmployeeStore = defineStore('employee', {
  state: () => ({
    list: [] as ExtendedEmployee[],
    currentStaff: undefined as ExtendedEmployee | undefined,
    _subscription: null as Subscription | null,
    employeeService: null as EmployeeService | null,
    roleService: null as EmployeeRoleService | null,
  }),
  actions: {
    async load() {
      if (getPlatform() === 'mobile-web') return;
      const staff = secureStorage.getItem(STAFF_KEY);
      this.currentStaff = staff ? (JSON.parse(secureStorage.getItem(STAFF_KEY) || '{}') as Employee) : undefined;
      if (this.currentStaff) await useAcl().setEmployeeRole(this.currentStaff);
    },
    async getService() {
      if (!this.employeeService)
        // service singleton is method-only, skip deep proxy
        this.employeeService = markRaw(await ServicesBuilder.getInstance().getService<EmployeeService>(EmployeeService));
      return this.employeeService;
    },
    async getRoleService() {
      if (!this.roleService)
        // service singleton is method-only, skip deep proxy
        this.roleService = markRaw(await ServicesBuilder.getInstance().getService<EmployeeRoleService>(EmployeeRoleService));
      return this.roleService;
    },
    async startLiveQuery() {
      if (getPlatform() === 'mobile-web') return;
      if (this._subscription) return;
      const service = await this.getService();
      const roleService = await this.getRoleService();
      if (this._subscription) return; // re-check after await to close TOCTOU race window
      const sub: Subscription = service
        .liveQuery(() => service.table.filter((e: Employee) => e.deleted_at === undefined || e.deleted_at === null).toArray())
        .subscribe({
          next: async (resultPromise) => {
            const result = await resultPromise;
            this.list = await Promise.all((result || []).map((item: Employee) => enrichEmployeeRole(item, roleService)));
          },
          // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
          error: (err) => { logLiveQueryError(err, { store: 'employee' }); if (isLiveQueryCursorError(err) && this._subscription === sub) { sub.unsubscribe(); this._subscription = null; } },
        });
      this._subscription = markRaw(sub);
    },
    stopLiveQuery() {
      this._subscription?.unsubscribe();
      this._subscription = null;
    },
    async getList() {
      const service = await this.getService();
      const roleService = await this.getRoleService();
      const result = await service.getAll();
      this.list = await Promise.all(
        (result || []).filter((e: Employee) => e.deleted_at === undefined || e.deleted_at === null).map((item) => enrichEmployeeRole(item, roleService)),
      );
      return this.list;
    },
    async update(employee: Employee) {
      const service = await this.getService();
      return await service.updateOne(employee.id_in_server, employee);
    },
    async save(employee: Employee) {
      const service = await this.getService();
      console.log('employee', employee);
      return await service.createEmployee(employee);
    },
    async delete(model: Employee) {
      const service = await this.getService();
      return await service.deleteOne(model);
    },
    async login(userUid: string, password: string) {
      const service = await this.getService();
      return await service.login(userUid, password);
    },
    async logout() {
      const service = PresentationService.getInstance();
      await service.disable();

      secureStorage.removeItem(STAFF_KEY);
      this.currentStaff = undefined;
      useAcl().stopEmployeeRoleLiveQuery();
    },
    async setEmployee(staff: Employee) {
      secureStorage.setItem(STAFF_KEY, JSON.stringify(staff));
      await this.load();
    },
    async changePassword(password: string) {
      const service = await this.getService();
      return await service.changePassword(password);
    },
  },
});
